import { useEffect, useRef } from "react";

type Options = {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage?: () => void;
  /** Px from bottom that counts as "near end". */
  bottomOffset?: number;
};

/**
 * Load the next infinite-query page only after the user scrolls near the bottom.
 * Consumes one scroll gesture per page so a still-visible sentinel cannot drain every page on open.
 */
export function useScrollLoadMore(scrollEl: HTMLElement | null, options: Options) {
  const { hasNextPage, isFetchingNextPage, fetchNextPage, bottomOffset = 64 } = options;
  const allowFetchRef = useRef(false);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!scrollEl || !fetchNextPage) return;

    const tryLoadMore = () => {
      const { hasNextPage: more, isFetchingNextPage: fetching, fetchNextPage: load, bottomOffset: offset = 64 } =
        optionsRef.current;
      if (!more || fetching || !load || !allowFetchRef.current) return;
      const { scrollTop, clientHeight, scrollHeight } = scrollEl;
      // Ignore the initial layout pass (scrollTop === 0) so open does not prefetch.
      if (scrollTop <= 0) return;
      if (scrollTop + clientHeight < scrollHeight - offset) return;
      allowFetchRef.current = false;
      load();
    };

    const onScroll = () => {
      allowFetchRef.current = true;
      tryLoadMore();
    };

    scrollEl.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      scrollEl.removeEventListener("scroll", onScroll);
      allowFetchRef.current = false;
    };
  }, [scrollEl, fetchNextPage, hasNextPage, isFetchingNextPage, bottomOffset]);
}
